import { Fragment, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler, ErreurApi } from "../../api/client";
import { useApi } from "../../api/hooks";
import { useSession } from "../../auth/Session";
import { EtatChargement, EtatErreur } from "../../composants/Avis";
import type { EcranProps } from "../types";

const AnalyseSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  nomFichier: z.string(),
  cheminLogique: z.string(),
  nature: z.string().nullable(),
  etat: z.enum(["EnAttente", "EnCours", "Proposee", "Echouee", "Confirmee", "Rejetee"]),
  modele: z.string().nullable(),
  propositionJson: z.string().nullable(),
  confirmationJson: z.string().nullable(),
  erreur: z.string().nullable(),
  demandeeLe: z.string(),
  repondueLe: z.string().nullable(),
  trancheeLe: z.string().nullable(),
  trancheePar: z.string().nullable(),
});

const ReponseSchema = z.object({
  analyses: z.array(AnalyseSchema),
  /** Faux quand la clé d'API manque : « éteint » ne se confond pas avec « rien à traiter ». */
  disponible: z.boolean(),
});

const ChampSchema = z.object({
  cle: z.string(),
  libelle: z.string(),
  type: z.string(),
  aide: z.string(),
});

type Analyse = z.infer<typeof AnalyseSchema>;

const LIBELLE_ETAT: Record<Analyse["etat"], string> = {
  EnAttente: "En attente",
  EnCours: "Lecture en cours",
  Proposee: "À confirmer",
  Echouee: "Échec",
  Confirmee: "Confirmée",
  Rejetee: "Rejetée",
};

/** Ce que le modèle a proposé, sous une forme exploitable par le formulaire. */
function proposition(a: Analyse): { champs: Record<string, unknown>; confiance?: number; remarque?: string } {
  if (!a.propositionJson) return { champs: {} };
  try {
    const brut = JSON.parse(a.propositionJson) as Record<string, unknown>;
    return {
      champs: (brut.champs as Record<string, unknown>) ?? {},
      confiance: typeof brut.confiance === "number" ? brut.confiance : undefined,
      remarque: typeof brut.remarque === "string" ? brut.remarque : undefined,
    };
  } catch {
    // Un JSON illisible en base ne doit pas faire tomber l'écran entier : la
    // ligne s'affiche, sans proposition, et reste traitable à la main.
    return { champs: {} };
  }
}

function Formulaire({
  dossierId,
  analyse,
  onFait,
}: {
  dossierId: string;
  analyse: Analyse;
  onFait: () => void;
}) {
  const p = proposition(analyse);

  const champs = useApi(
    ["champs-analyse", analyse.nature ?? "aucune"],
    `/dossiers/${dossierId}/analyses/champs?nature=${encodeURIComponent(analyse.nature ?? "")}`,
    z.array(ChampSchema),
  );

  const [valeurs, setValeurs] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(p.champs).map(([k, v]) => [k, v === null || v === undefined ? "" : String(v)]),
    ),
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function trancher(rejeter: boolean) {
    setErreur(null);
    setEnCours(true);
    try {
      await appeler(
        `/dossiers/${dossierId}/analyses/${analyse.id}`,
        z.object({ etat: z.string() }),
        {
          method: "PUT",
          body: JSON.stringify({
            // Un champ laissé vide part à `null`, jamais à "". Une chaîne vide
            // se lirait comme une valeur constatée ; `null` dit « non renseigné ».
            champs: Object.fromEntries(
              Object.entries(valeurs).map(([k, v]) => [k, v.trim() === "" ? null : v.trim()]),
            ),
            rejeter,
          }),
        },
      );
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? (e.motif ?? e.message) : "Échec.");
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="analyse__formulaire">
      {p.confiance !== undefined && (
        <p className="note">
          Confiance annoncée par le modèle : {Math.round(p.confiance * 100)} %.{" "}
          <strong>Ce n'est pas une garantie</strong> — un modèle sûr de lui peut se tromper.
        </p>
      )}
      {p.remarque && <p className="note">Remarque du modèle : {p.remarque}</p>}

      {(champs.data ?? []).map((c) => (
        <label key={c.cle}>
          {c.libelle}
          <input
            value={valeurs[c.cle] ?? ""}
            onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })}
            placeholder={c.aide}
          />
        </label>
      ))}

      {erreur && <p className="alerte" role="alert">{erreur}</p>}

      <div className="depot__actions">
        <button type="button" className="bouton-creux" disabled={enCours} onClick={() => void trancher(true)}>
          Rejeter
        </button>
        <button type="button" className="bouton-plein" disabled={enCours} onClick={() => void trancher(false)}>
          {enCours ? "Enregistrement…" : "Confirmer"}
        </button>
      </div>
    </div>
  );
}

/**
 * §7.4 — Les propositions de lecture automatique, et leur confirmation.
 *
 * ⛔ CE QU'UN MODÈLE PROPOSE N'EST JAMAIS UN FAIT.
 *
 * Un modèle ne lit pas : il produit le texte le plus vraisemblable. Sur une
 * facture mal scannée il rendra un montant plausible plutôt que d'avouer son
 * échec — et un montant plausible est indiscernable d'un montant juste. C'est
 * pour cela que cet écran existe : rien n'entre en comptabilité sans qu'un
 * humain ait relu et tranché.
 */
export function LectureAutomatique({ dossierId }: EcranProps) {
  const qc = useQueryClient();
  const { session } = useSession();
  const [ouverte, setOuverte] = useState<string | null>(null);

  const q = useApi(["analyses", dossierId], `/dossiers/${dossierId}/analyses`, ReponseSchema);

  if (q.isLoading) return <EtatChargement quoi="des lectures automatiques" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  const analyses = q.data?.analyses ?? [];
  const aConfirmer = analyses.filter((a) => a.etat === "Proposee");

  return (
    <div>
      <p className="resume">
        Chaque PDF déposé est lu par un modèle, qui <strong>propose</strong> les
        informations de la pièce. Rien n'est comptabilisé avant confirmation :
        un modèle rend toujours une réponse, y compris quand il n'a pas su lire.
      </p>

      {!q.data?.disponible && (
        <p className="alerte" role="alert">
          La lecture automatique est éteinte : clé d'API absente, ou{" "}
          <code>Ia:Actif</code> à faux. Les pièces sont déposées normalement,
          mais aucune n'est analysée.
        </p>
      )}

      <p className="note">
        {aConfirmer.length} pièce(s) à confirmer sur {analyses.length} analysée(s).
      </p>

      <table className="tableau">
        <thead>
          <tr>
            <th>Pièce</th>
            <th>Nature</th>
            <th>État</th>
            <th>Modèle</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {analyses.map((a) => (
            <Fragment key={a.id}>
              <tr>
                <td title={a.cheminLogique}>{a.nomFichier}</td>
                <td>{a.nature ?? "—"}</td>
                <td>
                  <span className="jeton">{LIBELLE_ETAT[a.etat]}</span>
                  {a.erreur && <div className="note">{a.erreur}</div>}
                  {a.trancheePar && <div className="note">par {a.trancheePar}</div>}
                </td>
                <td className="note">{a.modele ?? "—"}</td>
                <td>
                  {/* Trancher est réservé au gérant : c'est lui qui engage le
                      cabinet sur les chiffres retenus. */}
                  {a.etat === "Proposee" && session?.role === "admin" && (
                    <button
                      type="button"
                      className="bouton-creux"
                      onClick={() => setOuverte(ouverte === a.id ? null : a.id)}
                    >
                      {ouverte === a.id ? "Fermer" : "Relire"}
                    </button>
                  )}
                  {(a.etat === "Echouee" || a.etat === "Rejetee") && (
                    <button
                      type="button"
                      className="bouton-creux"
                      onClick={() =>
                        void appeler(
                          `/dossiers/${dossierId}/analyses/${a.documentId}`,
                          z.object({ statut: z.string() }),
                          { method: "POST" },
                        ).then(() => qc.invalidateQueries({ queryKey: ["analyses", dossierId] }))
                      }
                    >
                      Relancer
                    </button>
                  )}
                </td>
              </tr>

              {ouverte === a.id && (
                <tr>
                  <td colSpan={5}>
                    <Formulaire
                      dossierId={dossierId}
                      analyse={a}
                      onFait={() => {
                        setOuverte(null);
                        void qc.invalidateQueries({ queryKey: ["analyses", dossierId] });
                      }}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
